import type { VoidComponent } from "solid-js";
import { getOwner, onMount, runWithOwner } from "solid-js";
import { useTour } from "@ark-ui/solid";
import { useNavigate } from "@solidjs/router";
import { useTranslation } from "../../state/i18n";
import { noop } from "../../utils/noop";
import { DECORATIVE_IMAGE_ALT } from "../../constants";
import { isNil } from "lodash-es";
import posthog from "posthog-js";
import { POSTHOG_EVENTS } from "../../services/PostHogService";
import Tour, { type StepDetailsWithMeta } from "../../components/tour/Tour";
import { useFormEditorTour } from "./FormEditorTourProvider";
import TourModal from "../../components/modal/TourModal";
import tourIntroImage from "../../assets/editor/tour/intro.webp";
import tourStepOneImage from "../../assets/editor/tour/step-1.webp";
import tourStepTwoImage from "../../assets/editor/tour/step-2.webp";
import tourStepThreeImage from "../../assets/editor/tour/step-3.webp";

const FormEditorTour: VoidComponent = () => {
  const { t } = useTranslation();
  const owner = getOwner();
  const navigate = useNavigate();

  const { tourState, setTourState } = useFormEditorTour();

  const ensureOnPage = (page: string, show: () => void) => {
    runWithOwner(owner, () => {
      navigate(page, { replace: true });
      show();
    });
  };

  const steps: StepDetailsWithMeta[] = [
    {
      id: "step-1",
      type: "tooltip",
      target: () => document.querySelector("[data-tour-highlight-id='editor-menu']"),
      placement: "right-start",
      offset: { mainAxis: 4, crossAxis: 110 },
      title: t(($) => $.FORM_EDITOR_TOUR_STEP_1_TITLE),
      description: t(($) => $.FORM_EDITOR_TOUR_STEP_1_DESCRIPTION),
      meta: {
        image: tourStepOneImage,
      },
      actions: [
        { action: "next", label: t(($) => $.FORM_EDITOR_TOUR_ACTIONS_NEXT_LABEL), attrs: { variant: "primary" } },
      ],
      effect: ({ show }) => {
        ensureOnPage("../fields", show);
        return noop;
      },
    },
    {
      id: "step-2",
      type: "tooltip",
      target: () => document.querySelector("[data-tour-highlight-id='form-header']"),
      placement: "bottom-end",
      offset: { mainAxis: 4 },
      title: t(($) => $.FORM_EDITOR_TOUR_STEP_2_TITLE),
      description: t(($) => $.FORM_EDITOR_TOUR_STEP_2_DESCRIPTION),
      meta: {
        image: tourStepTwoImage,
      },
      actions: [
        { action: "prev", label: t(($) => $.FORM_EDITOR_TOUR_ACTIONS_PREVIOUS_LABEL) },
        { action: "next", label: t(($) => $.FORM_EDITOR_TOUR_ACTIONS_NEXT_LABEL), attrs: { variant: "primary" } },
      ],
      effect: ({ show }) => {
        ensureOnPage("../fields", show);
        return noop;
      },
    },
    {
      id: "step-3",
      type: "tooltip",
      target: () => document.querySelector("[data-tour-highlight-id='publish-button']"),
      placement: "bottom-end",
      offset: { mainAxis: 4 },
      title: t(($) => $.FORM_EDITOR_TOUR_STEP_3_TITLE),
      description: t(($) => $.FORM_EDITOR_TOUR_STEP_3_DESCRIPTION),
      meta: {
        image: tourStepThreeImage,
      },
      actions: [
        { action: "prev", label: t(($) => $.FORM_EDITOR_TOUR_ACTIONS_PREVIOUS_LABEL) },
        { action: "dismiss", label: t(($) => $.FORM_EDITOR_TOUR_ACTIONS_FINISH_LABEL), attrs: { variant: "primary" } },
      ],
      effect: ({ show }) => {
        ensureOnPage("../fields", show);
        return noop;
      },
    },
  ];

  const tour = useTour({
    steps,
    preventInteraction: true,
    keyboardNavigation: true,
    closeOnInteractOutside: false,
    translations: {
      progressText: ({ current, total }) => t(($) => $.FORM_EDITOR_TOUR_PROGRESS, { current: current + 1, total }),
    },
    onStatusChange: ({ status, stepId }) => {
      // Redirect user to initial form editor page when tour is closed
      if (status === "completed" || status === "dismissed") {
        navigate("../fields", { replace: true });
      }

      if (status === "started") {
        setTourState({ status: "in_progress" });
        posthog.capture(POSTHOG_EVENTS.EDITOR_TOUR_STARTED);
      } else if (status === "dismissed") {
        setTourState({ status: "aborted", lastStep: undefined });
        posthog.capture(POSTHOG_EVENTS.EDITOR_TOUR_ABORTED, { $set: { stepId } });
      } else if (status === "completed") {
        setTourState({ status: "completed", lastStep: undefined });
        posthog.capture(POSTHOG_EVENTS.EDITOR_TOUR_COMPLETED);
      }
    },
    onStepChange: ({ stepId }) => {
      if (!isNil(stepId)) {
        setTourState({ lastStep: stepId });
      }
    },
    spotlightOffset: { x: 6, y: 6 },
  });

  onMount(() => {
    // Start tour at last step when browser window has been closed during tour
    if (tourState.status === "in_progress") {
      tour().start(tourState.lastStep);
    }
  });

  const skipTour = () => {
    setTourState({ status: "skipped" });
    posthog.capture(POSTHOG_EVENTS.EDITOR_TOUR_SKIPPED);
  };

  return (
    <>
      <TourModal
        open={tourState.status === "not_started"}
        image={{ src: tourIntroImage, alt: DECORATIVE_IMAGE_ALT }}
        title={t(($) => $.FORM_EDITOR_TOUR_INTRO_TITLE)}
        description={t(($) => $.FORM_EDITOR_TOUR_INTRO_DESCRIPTION)}
        onClose={skipTour}
        primaryAction={{
          label: t(($) => $.FORM_EDITOR_TOUR_INTRO_ACTIONS_START),
          onClick: () => tour().start(),
        }}
        secondaryAction={{
          label: t(($) => $.FORM_EDITOR_TOUR_INTRO_ACTIONS_SKIP),
          onClick: skipTour,
        }}
      />

      <Tour tour={tour} />
    </>
  );
};
export default FormEditorTour;
